/** The about page's videos are only ever moved by the scroll: they must never
 *  play by themselves. They're played once, for a moment, muted (iOS draws a
 *  video's frames only after it has played); any other playing, whatever
 *  starts it (a play() that resolves late, the page coming back from the
 *  background, an audio interruption ending, the browser resuming it), is
 *  stopped at once, and kept stopped. */
export function keepStill(v: HTMLVideoElement) {
    let priming = 0
    // Belt and braces: it's also slowed right down, for good (1/16th speed), so
    // whatever plays it, and before anything stops it, it barely moves (a
    // fraction of a frame in the time it takes to be stopped). A new file
    // (load) resets the speed, so it's set again whenever one starts.
    const SLOW = [0.0625, 0.1, 0.25]
    const slow = () => {
        for (const r of SLOW) {
            try {
                if (v.defaultPlaybackRate !== r) v.defaultPlaybackRate = r
                if (v.playbackRate !== r) v.playbackRate = r
                return
            } catch {}
        }
    }
    slow()
    const stop = () => {
        if (v.playbackRate > 0.3) slow()
        if (!priming && !v.paused) v.pause()
    }
    const events = ["play", "playing", "timeupdate", "ratechange", "loadstart", "loadedmetadata", "emptied"]
    for (const e of events) v.addEventListener(e, stop)
    const docEvents: [EventTarget, string][] = [
        [document, "visibilitychange"],
        [window, "pageshow"],
        [window, "focus"],
    ]
    for (const [t, e] of docEvents) t.addEventListener(e, stop)
    // (and a watch, should none of those come)
    const watch = setInterval(stop, 250)

    /** the moment's play, for iOS: over when it's begun (or failed, or after
     *  a little while if it neither), paused */
    const prime = () => {
        if (!v.play) return
        const k = ++priming
        const end = () => {
            if (priming !== k) return
            priming = 0
            if (!v.paused) v.pause()
        }
        slow()
        const late = setTimeout(end, 400)
        let p: Promise<void> | undefined
        try {
            p = v.play()
        } catch {}
        if (p && p.then) p.then(end, end).finally(() => clearTimeout(late))
        else (clearTimeout(late), end())
    }
    // Back from the background (another app, the tab switcher, the phone locked):
    // iOS lets go of a video's decoded frames meanwhile, and a video that never
    // plays never gets them back, so it stays blank. So on the way back it's
    // given its moment's play again (and, if the browser dropped the file
    // altogether, the file again), and the page puts it back where the scroll is
    // (the pause at the end of that moment).
    let reviving = 0
    const revive = () => {
        if (document.visibilityState === "hidden") return
        clearTimeout(reviving)
        reviving = window.setTimeout(() => {
            if (v.error || v.readyState < 2) {
                const s = v.currentSrc || v.src
                if (s) {
                    v.src = s
                    v.load()
                }
            }
            prime()
        }, 60)
    }
    const backEvents: [EventTarget, string][] = [
        [document, "visibilitychange"],
        [window, "pageshow"],
        [window, "focus"],
    ]
    for (const [t, e] of backEvents) t.addEventListener(e, revive)
    const release = () => {
        clearInterval(watch)
        clearTimeout(reviving)
        for (const [t, e] of backEvents) t.removeEventListener(e, revive)
        for (const e of events) v.removeEventListener(e, stop)
        for (const [t, e] of docEvents) t.removeEventListener(e, stop)
        priming = 0
        if (!v.paused) v.pause()
    }
    return { prime, release }
}
